import React, { useState, useEffect } from "react";
import axios from "axios";
import { AuthGuard } from "../App";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom"; // Import useNavigate for the back button
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMapMarkerAlt,
  faStar,
  faChevronLeft,
  faSpinner, // For loading indicator
  faExclamationCircle, // For error indicator
  faHeart, // For Add to Favorites button
  faCalendarCheck, // For Add Reservation button
  faExpand, // For photo modal icon
} from "@fortawesome/free-solid-svg-icons";

const SpecificRentalPage = () => {
  const [rentalInfo, setRentalInfo] = useState(null); // Initialize as null to handle loading state
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [ratings, setRatings] = useState(0); // Default to 0
  const [amenities, setAmenities] = useState([]);
  const [selectedPhoto, setSelectedPhoto] = useState(null); // State to track selected photo
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false); // State to track if the photo modal is open
  const [loading, setLoading] = useState(true); // New loading state
  const [error, setError] = useState(null); // New error state

  const navigate = useNavigate(); // Hook for navigation
  const token = localStorage.getItem("token");
  const userDataString = localStorage.getItem("user");
  // Safely parse userData, handle case where it might be null or invalid
  const userData = userDataString ? JSON.parse(userDataString) : null;
  const userId = userData?.unique; // Safely access unique property

  const rentalId = localStorage.getItem("specificRentalId");

  // Helper function to fetch amenities (made async for consistency)
  const fetchAmenities = async (propertyId) => {
    if (!propertyId || !token) return; // Basic validation
    try {
      const response = await axios.get(
        `https://backend.inyumba.co.ke/iNyumba/amenities/fetch/${propertyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAmenities(response.data.response);
    } catch (error) {
      console.error("Error fetching amenities:", error);
      // No toast here as it's a secondary fetch, not critical for initial load
    }
  };

  // Helper function to fetch ratings (made async for consistency)
  // Note: The previous code passed rentalId to fetchRatings, but fetched property_id from rentalInfo.
  // Assuming propertyId is the correct parameter for the backend endpoint.
  const fetchRatings = async (propertyId) => {
    if (!propertyId || !token) return; // Basic validation
    try {
      const response = await axios.get(
        `https://backend.inyumba.co.ke/iNyumba/admin/comments/average-ratings/${propertyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRatings(response.data.response || 0); // Default to 0 if response.data.response is null/undefined
    } catch (error) {
      console.error("Error fetching ratings:", error);
      setRatings(0); // Explicitly set to 0 on error
    }
  };

  useEffect(() => {
    // Initial checks before fetching data
    if (!rentalId) {
      setError("No rental selected. Please navigate from the marketplace.");
      setLoading(false);
      toast.error("Error: No rental ID found in local storage.");
      return;
    }
    if (!token || !userId) {
      setError("Authentication or user data missing. Please log in.");
      setLoading(false);
      toast.error("Authentication required. Please log in.");
      return;
    }

    setLoading(true); // Start loading
    setError(null); // Clear any previous errors

    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };

        // Fetch rental information first as other fetches depend on its property_id
        const rentalResponse = await axios.get(
          `https://backend.inyumba.co.ke/iNyumba/admin/rentals/specific/${rentalId}`,
          { headers }
        );
        setRentalInfo(rentalResponse.data.response);

        const propertyId = rentalResponse.data.response.property_id;

        // Fetch amenities and ratings concurrently after getting property_id
        await Promise.all([
          fetchAmenities(propertyId),
          fetchRatings(propertyId),
        ]);

        // Fetch photos and videos concurrently
        await Promise.all([
          axios.get(`https://backend.inyumba.co.ke/iNyumba/photos/fetch/${rentalId}`, { headers })
            .then(res => setPhotos(res.data.response))
            .catch(err => console.error("Error fetching photos:", err)), // Catch individually
          axios.get(`https://backend.inyumba.co.ke/iNyumba/videos/fetch/${rentalId}`, { headers })
            .then(res => setVideos(res.data.response))
            .catch(err => console.error("Error fetching videos:", err)), // Catch individually
        ]);

        setLoading(false); // End loading
      } catch (err) {
        console.error("Error fetching rental details:", err);
        setError("Failed to load rental details. Please try again later.");
        setLoading(false); // End loading even on error
        toast.error(err.response?.data?.response || "Failed to load rental details.");
      }
    };

    fetchData();
  }, [rentalId, token, userId]); // Depend on rentalId, token, userId

  const handleAddReservation = async () => {
    if (!userId || !rentalId || !token) {
      toast.error("User, rental, or authentication data missing.");
      return;
    }
    try {
      const response = await axios.post(
        `https://backend.inyumba.co.ke/iNyumba/user/reservations/add/${userId}/${rentalId}`,
        null, // No request body needed
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.response);
    } catch (error) {
      console.error("Error adding reservation:", error);
      toast.error(error.response?.data?.response || "Failed to add reservation.");
    }
  };

  const handleAddFavourites = async () => {
    if (!userId || !rentalId || !token) {
      toast.error("User, rental, or authentication data missing.");
      return;
    }
    try {
      const response = await axios.post(
        `https://backend.inyumba.co.ke/iNyumba/favorite/users/${userId}/favorites/${rentalId}`,
        {}, // Empty object for request body if backend expects it
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.response);
    } catch (error) {
      console.error("Error adding to favorites:", error);
      toast.error(error.response?.data?.response || "Failed to add to favorites.");
    }
  };

  // Function to render star ratings dynamically
  const renderStars = (rating) => {
    const stars = [];
    // Ensure rating is a number and not null/undefined
    const numericRating = rating !== null && rating !== undefined ? parseFloat(rating) : 0;
    const fullStars = Math.floor(numericRating);
    const hasHalfStar = (numericRating % 1) > 0.25 && (numericRating % 1) < 0.75; // Render half star if fractional part is between 0.25 and 0.75

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <FontAwesomeIcon key={`full-${i}`} icon={faStar} className="text-yellow-400" />
      );
    }
    if (hasHalfStar) {
      // Use a half-filled star icon or a slightly opaque full star
      stars.push(
        <FontAwesomeIcon key={`half-${numericRating}`} icon={faStar} className="text-yellow-400 opacity-50" />
      );
    }
    // Fill remaining with empty stars up to 5
    for (let i = stars.length; i < 5; i++) {
      stars.push(
        <FontAwesomeIcon key={`empty-${i}`} icon={faStar} className="text-gray-300" />
      );
    }
    return stars;
  };

  // Function to handle opening the modal with selected photo
  const openPhotoModal = (photo) => {
    setSelectedPhoto(photo);
    setIsPhotoModalOpen(true);
  };

  // Function to close the modal
  const closePhotoModal = () => {
    setSelectedPhoto(null);
    setIsPhotoModalOpen(false);
  };

  // --- Loading State UI ---
  if (loading) {
    return (
      <AuthGuard>
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
          <div className="flex flex-col items-center text-gray-600 p-8 rounded-lg bg-white shadow-lg border border-blue-200 animate-pulse">
            <FontAwesomeIcon icon={faSpinner} spin className="text-5xl mb-4 text-indigo-500" />
            <p className="text-xl font-medium">Loading rental details...</p>
          </div>
        </div>
      </AuthGuard>
    );
  }

  // --- Error State UI ---
  if (error) {
    return (
      <AuthGuard>
        <div className="min-h-screen flex flex-col items-center justify-center bg-red-50 p-6">
          <div className="flex flex-col items-center text-red-700 p-8 rounded-lg bg-white shadow-lg border border-red-200">
            <FontAwesomeIcon icon={faExclamationCircle} className="text-5xl mb-4" />
            <h2 className="text-2xl font-bold mb-2">Error Loading Rental</h2>
            <p className="text-center mb-4 text-lg">{error}</p>
            <button
              onClick={() => navigate(-1)} // Go back to previous page
              className="bg-red-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-red-700 transition-colors shadow-md"
            >
              Go Back
            </button>
          </div>
        </div>
      </AuthGuard>
    );
  }

  // Fallback if rentalInfo is unexpectedly null after loading (e.g., API returned empty data)
  if (!rentalInfo || Object.keys(rentalInfo).length === 0) {
    return (
      <AuthGuard>
        <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-6">
          <div className="flex flex-col items-center text-gray-600 p-8 rounded-lg bg-white shadow-lg border border-gray-200">
            <FontAwesomeIcon icon={faExclamationCircle} className="text-5xl mb-4 text-gray-500" />
            <h2 className="text-2xl font-bold mb-2">Rental Not Found</h2>
            <p className="text-center mb-4 text-lg">The rental unit you are looking for does not exist or could not be loaded.</p>
            <button
              onClick={() => navigate(-1)}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-md"
            >
              Go Back
            </button>
          </div>
        </div>
      </AuthGuard>
    );
  }

  return (
    <AuthGuard>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-100 p-6 sm:p-8 lg:p-10">
        
        {/* Back Button */}
        <div className="mb-8 animate-fade-in-down">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center px-4 py-2 bg-white text-gray-800 rounded-full shadow-md hover:bg-gray-100 transition-colors transform hover:-translate-x-1"
            aria-label="Back to Previous Page"
          >
            <FontAwesomeIcon icon={faChevronLeft} className="mr-2" />
            Back
          </button>
        </div>

        {/* Rental Details Section */}
        <section className="bg-white rounded-2xl shadow-2xl overflow-hidden max-w-5xl mx-auto mb-12 animate-fade-in-up">
          <div className="relative h-72 sm:h-80 lg:h-96">
            <img
              src={rentalInfo.photo}
              alt="Rental Main"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-6 sm:p-8">
              <div className="text-white">
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-1 leading-tight drop-shadow-md">
                  {rentalInfo.house_type} - House No. {rentalInfo.house_number}
                </h1>
                <p className="text-md sm:text-lg lg:text-xl font-medium flex items-center drop-shadow-sm">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2 text-indigo-300" />
                  {rentalInfo.property_name} (Located in {rentalInfo.property_location})
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 sm:p-8">
            {/* Price & Rating */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 pb-4 border-b border-gray-100">
              <div className="text-3xl sm:text-4xl font-extrabold text-green-600 mb-4 sm:mb-0">
                KES {Number(rentalInfo.rent_price).toLocaleString()}
                <span className="text-lg text-gray-500 font-medium ml-2">/month</span>
              </div>
              <div className="flex items-center text-lg sm:text-xl font-bold text-gray-800 bg-yellow-50 px-4 py-2 rounded-full shadow-sm">
                <span className="mr-2">Rating:</span>
                {renderStars(ratings)}
                <span className="ml-2">
                  {ratings !== null ? `${ratings.toFixed(1)}/5` : "N/A"}
                </span>
              </div>
            </div>

            {/* Description & Vacancy */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">Description</h2>
                <p className="text-gray-700 text-base leading-relaxed">
                  {rentalInfo.description}
                </p>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">Status</h2>
                <p className="text-xl font-semibold flex items-center">
                  <span
                    className={`px-4 py-2 rounded-full text-white ${
                      rentalInfo.status === "Available"
                        ? "bg-green-500"
                        : "bg-red-500"
                    }`}
                  >
                    {rentalInfo.status}
                  </span>
                </p>
              </div>
            </div>

            {/* Amenities Section */}
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 text-center">
              Amenities
            </h2>
            {amenities.length > 0 ? (
              <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-8">
                {amenities.map((amenity, index) => (
                  <span
                    key={index}
                    className="bg-indigo-50 text-indigo-800 text-sm sm:text-base font-medium px-4 py-2 rounded-full shadow-sm transition-colors duration-200 hover:bg-indigo-100"
                  >
                    {amenity.amenity_name}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-600 mb-8 text-md sm:text-lg">No specific amenities listed for this rental unit.</p>
            )}
          </div>
        </section>

        {/* Photos Section */}
        <section className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 max-w-5xl mx-auto mb-12 animate-fade-in-up">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 text-center">
            Gallery Photos
          </h2>
          {photos.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
              {photos.map((photo, index) => (
                <div
                  key={index}
                  className="relative w-full h-36 sm:h-48 rounded-lg overflow-hidden shadow-md cursor-pointer
                             transition-all duration-300 transform hover:scale-105 hover:shadow-xl group"
                  onClick={() => openPhotoModal(photo)}
                >
                  <img
                    src={photo.photo_url}
                    alt={`Rental Photo ${index + 1}`}
                    className="w-full h-full object-cover group-hover:brightness-75 transition-all duration-300"
                  />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <FontAwesomeIcon icon={faExpand} className="text-white text-3xl drop-shadow-lg" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-600 text-lg py-10">No additional photos available for this rental.</p>
          )}
        </section>

        {/* Videos Section */}
        <section className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 max-w-5xl mx-auto mb-12 animate-fade-in-up">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 text-center">
            Walkthrough Videos
          </h2>
          {videos.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {videos.map((video, index) => (
                <div key={index} className="w-full rounded-lg overflow-hidden shadow-lg aspect-video">
                  <iframe
                    title={`Rental Video ${index + 1}`}
                    src={video.video_url}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-600 text-lg py-10">No walkthrough videos available for this rental.</p>
          )}
        </section>

        {/* Action Buttons */}
        {rentalInfo.status === "OPEN" && ( // Changed "Available" back to "OPEN"
          <div className="mt-8 max-w-3xl mx-auto flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4 animate-fade-in-up">
            <button
              onClick={handleAddReservation}
              className="flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg shadow-lg transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
            >
              <FontAwesomeIcon icon={faCalendarCheck} className="mr-3 text-xl" />
              Add Reservation
            </button>
            <button
              onClick={handleAddFavourites}
              className="flex items-center justify-center bg-gray-700 hover:bg-gray-800 text-white font-bold py-3 px-6 rounded-lg shadow-lg transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50"
            >
              <FontAwesomeIcon icon={faHeart} className="mr-3 text-xl" />
              Add to Favorites
            </button>
          </div>
        )}

        {/* Photo Modal */}
        {isPhotoModalOpen && selectedPhoto && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 backdrop-blur-sm transition-opacity duration-300 ease-in-out"
            onClick={closePhotoModal}
          >
            <div
              className="relative max-w-full md:max-w-screen-lg lg:max-w-screen-xl p-4 md:p-8"
              onClick={(e) => e.stopPropagation()} // Prevent modal close on inner click
            >
              <img
                src={selectedPhoto.photo_url}
                alt="Enlarged Rental Photo"
                className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl border-2 border-white animate-zoom-in"
              />
              <button
                className="absolute top-4 right-4 bg-white text-gray-900 rounded-full p-3 shadow-lg hover:bg-gray-200 transition-colors transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-gray-300"
                onClick={closePhotoModal}
                aria-label="Close Modal"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </AuthGuard>
  );
};

export default SpecificRentalPage;